/**
 * Clears all authentication and session data from localStorage and sessionStorage.
 *
 * SAM-240: this used to remove a curated list of keys (token, user, businessData,
 * selectedBusinessId, ...) but missed `accessToken` and `refreshToken` in
 * localStorage and `selectedTenantId` in sessionStorage — all written by the
 * REST auth flow (see src/utils/token.ts, src/services/authService.ts). That
 * residue survived an expired-session cleanup and could leave the next login
 * attempt reading stale auth state. A full wipe is the only way to guarantee no
 * leftover key can affect the next login, matching the SAM-240 requirement to
 * clear localStorage and sessionStorage completely before showing the login
 * screen. Every key currently in use (token/accessToken/refreshToken,
 * businessData, selectedBusinessId, selectedTenantId, user, authData) is auth
 * or tenant/business scoped and safe to drop on logout or session expiration.
 */
export const clearAllStorage = (): void => {
  try {
    localStorage.clear();
  } catch (error) {
    console.error('Error clearing localStorage:', error);
  }

  try {
    sessionStorage.clear();
  } catch (error) {
    console.error('Error clearing sessionStorage:', error);
  }
};

/**
 * Force clear all storage and reload the page.
 * Use this as a nuclear option when there are persistent auth conflicts.
 */
export const forceAuthReset = (): void => {
  clearAllStorage();

  // Reload the page to ensure clean state
  window.location.href = '/';
};

/**
 * Log Out (SAM-1320, B7): signs out, wipes the storage and reloads. Clearing only the storage left the previous
 * session's state in memory (business in BusinessContext, the query cache), so the next account in the same tab
 * started in the earlier one's business and its first `POST /invitation` was a 403. A reload drops all of it.
 */
export const endSession = async (signOutFn: () => Promise<unknown>): Promise<void> => {
  try {
    await signOutFn();
  } catch (error) {
    console.error('Error signing out:', error);
  } finally {
    forceAuthReset();
  }
};
